import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Grid, Stack, Typography } from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PaymentsIcon from "@mui/icons-material/Payments";
import PhoneIcon from "@mui/icons-material/Phone";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { useGetProvidersQuery, useGetWhatsappSettingsQuery } from "../../../Api/platformApi";
import {
  AppPage, PageHeader, CardSurface, StatusBadge,
} from "../../../Components/ui";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";

const API_URL = process.env.REACT_APP_API_URL;

const IntegrationsCenter: React.FC = () => {
  const { data: providersData } = useGetProvidersQuery();
  const { data: waData } = useGetWhatsappSettingsQuery();

  const [isSuper, setIsSuper] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.post(`${API_URL}/user/userInfo`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    }).then((res) => { if (!cancelled) setIsSuper(Boolean(res.data?.data?.is_super_admin)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const providers = providersData?.data ?? [];
  // For super admins getProviders returns every tenant's providers, so a plain
  // .some() would read "Connected" the moment ANY company has Telnyx. Count the
  // distinct connected companies instead; company users only see their own row.
  const telnyxCompanies = new Set(
    providers
      .filter((p: any) => p.provider_type === "sms" && p.provider_name === "telnyx" && p.active)
      .map((p: any) => p.company_id),
  );
  const hasSmsTelnyx = telnyxCompanies.size > 0;
  const waConfigured = waData?.data?.provider_configured;

  const cards = [
    {
      key: "telnyx", name: "Telnyx SMS", icon: <SmsIcon />,
      state: hasSmsTelnyx ? "connected" : "disconnected",
      detail: isSuper
        ? (hasSmsTelnyx ? `Connected companies: ${telnyxCompanies.size}` : "No companies connected")
        : "Primary SMS provider",
    },
    { key: "whatsapp", name: "WhatsApp Business", icon: <WhatsAppIcon />, state: waConfigured ? "connected" : "disconnected", detail: "Via Telnyx WhatsApp" },
    { key: "bambora", name: "Bambora", icon: <PaymentsIcon />, state: "connected", detail: "Billing & payments gateway" },
    { key: "3cx", name: "3CX Voice", icon: <PhoneIcon />, state: "soon", detail: "VoIP calling — Phase 9" },
    { key: "ai", name: "AI Receptionist", icon: <SmartToyIcon />, state: "soon", detail: "AI replies — Phase 10" },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<HubRoundedIcon />}
          title="Integrations Center"
          subtitle="Provider connections. Per-company connected systems (CRMs, websites) are managed on each company's profile."
        />
      }
    >
      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={4} key={c.key}>
            <CardSurface
              sx={{
                p: 2.5,
                opacity: c.state === "soon" ? 0.72 : 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(11,30,45,0.06)",
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(c.icon, { sx: { fontSize: 22, color: NAVY } })}
                </Box>
                <Box flexGrow={1} minWidth={0}>
                  <Typography fontSize={14} fontWeight={700} color={NAVY} noWrap>
                    {c.name}
                  </Typography>
                  <Typography fontSize={12} color={TEXT_SECONDARY} noWrap>
                    {c.detail}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <StatusBadge
                  status={c.state === "soon" ? "coming_soon" : c.state}
                  map={{ coming_soon: "neutral", disconnected: "warning", connected: "success" }}
                  label={
                    c.state === "soon"
                      ? "Coming Soon"
                      : c.state === "connected"
                      ? "Connected"
                      : "Not Configured"
                  }
                />
              </Box>
            </CardSurface>
          </Grid>
        ))}
      </Grid>
    </AppPage>
  );
};

export default IntegrationsCenter;
